import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CC, ccConfig } from './CC';
import { zip, unzip, lzw_encode, decompressFromURI } from '../Helpers/Zip';
import * as ccUtils from './utils';

export type User = {
  userDetails: {
    email: string;
    realname: string;
    roles: string[];
  };
  token: string;
  tokenExpires: string;
  albums: Album[];
  baskets: Basket[];
};

export type Album = {
  albumId: string;
  albumName: string;
  albumPath: string;
  albumParent: string;
  albumText: string;
  childNodes?: Album[];
  nodeType: 'text' | 'annotatedImage';
};

export type Basket = {
  basketId: string;
  basketName: string;
  created: string;  // Assuming this is returned as an ISO date string
  altered: string;  // Assuming this is returned as an ISO date string
  items: string[];  // Array of objectIds
};

export interface ICCContext {
  cc: CC;
  setCc: React.Dispatch<React.SetStateAction<CC>>;
  sort: string;
  setSort: React.Dispatch<React.SetStateAction<string>>;
  first: number;
  setFirst: React.Dispatch<React.SetStateAction<number>>;
  recordsPerPage: number;
  setRecordsPerPage: React.Dispatch<React.SetStateAction<number>>;
  oldRecordsPerPage: number;
  setOldRecordsPerPage: React.Dispatch<React.SetStateAction<number>>;
  viewMode: string;
  setViewMode: React.Dispatch<React.SetStateAction<string>>;
  browseMode: string;
  setBrowseMode: React.Dispatch<React.SetStateAction<string>>;
  data: { [key: string]: any };
  setData: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isReady: boolean;
  setReadyState: (state: boolean) => void;
  clearSearch: () => void;
  selectedRecord: number | null;
  setSelectedRecord: React.Dispatch<React.SetStateAction<number | null>>;
  setCCContext: (updates: Partial<Omit<ICCContext, 'setCCContext'>>) => void;
}

export const CCContext = createContext<ICCContext | undefined>(undefined);

interface CCProviderProps {
  children: React.ReactNode;
}

export const CCProvider: React.FC<CCProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(window.location.search);

  const stateParam = queryParams.get('state');
  const compressedStateParam = queryParams.get('cstate');
  const monument = queryParams.get('monument');
  const theme = queryParams.get('theme');
  const id = queryParams.get('id');
  const indexParam = queryParams.get('index');
  const viewModeParam = queryParams.get('viewmode');

  const defaultCCObj = new CC();

  // Define initial recordsPerPage to use in initialFirst calculation
  const recordsPerPageInitial = 64; // Default value

  // State variables
  const [cc, setCc] = useState<CC>(defaultCCObj);
  const [sort, setSort] = useState<string>(Object.keys(ccConfig.sort)[0]);
  const [first, setFirst] = useState<number>(1);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(recordsPerPageInitial);
  const [oldRecordsPerPage, setOldRecordsPerPage] = useState<number>(8);
  const [viewMode, setViewMode] = useState<string>("homepage");
  const [browseMode, setBrowseMode] = useState<string>("paginate");
  const [user, setUser] = useState<any | null>(null);
  const [data, setData] = useState<{ [key: string]: any }>({
    records: {
      record: []
    }
  });
  const [isReady, setIsReady] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<number | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  const prevCcPushed = useRef<string>("");
  const instanceRef = useRef({});

  const setReadyState = (state: boolean) => {
    setIsReady(state);
  };
  
  // Function to clear search
  const clearSearch = () => {
    const initialCC = new CC();
    setCc(initialCC);
    setFirst(1);
    setViewMode("homepage");
  };

  // Function to set CCContext
  const setCCContext = (updates: Partial<Omit<ICCContext, 'setCCContext'>>) => {
    if (updates.cc) setCc(updates.cc);
    if (updates.sort) setSort(updates.sort);
    if (updates.first) setFirst(updates.first);
    if (updates.recordsPerPage) setRecordsPerPage(updates.recordsPerPage);
    if (updates.oldRecordsPerPage) setOldRecordsPerPage(updates.oldRecordsPerPage);
    if (updates.viewMode) setViewMode(updates.viewMode);
    if (updates.browseMode) setBrowseMode(updates.browseMode);
    if (updates.data) setData(updates.data);
    if (updates.user) setUser(updates.user);
    if (updates.isReady !== undefined) setIsReady(updates.isReady);
    if (updates.selectedRecord !== undefined) {
      setTimeout(() => {
        if (updates && updates.selectedRecord) setSelectedRecord(updates.selectedRecord);
      }, 500);
    }
  };

  // Function to push state to URL
  const pushState = (currentState: {
    cc: CC;
    sort: string;
    first: number;
    recordsPerPage: number;
    viewMode: string;
    browseMode: string;
  }) => {
    const state = currentState;
    const queryParams = new URLSearchParams(window.location.search);

    if (prevCcPushed.current !== JSON.stringify(state)) {
      prevCcPushed.current = JSON.stringify(state);
      navigate(`${window.location.pathname}?${queryParams.toString()}`, { state });
    }
  };

  // Function to initialize CC based on URL parameters
  const initializeCC = async () => {
    let initialCCObj = defaultCCObj;
    let initialFirstLocal = 1;
    let initialSelectedRecordLocal: number | null = null;
    let initialViewModeLocal = "homepage";

    if (id) {
      initialViewModeLocal = 'list';
    }

    if (stateParam) {
      console.log('stateParam: ' + stateParam)
      initialViewModeLocal = "list";

      if (stateParam.startsWith('state_')) {
        // Handle asynchronous state retrieval
        try {
          const retrievedCcState = await ccUtils.getStateFromServer(stateParam);
          initialCCObj = ccUtils.deepMerge(defaultCCObj, retrievedCcState);

          // After merging, check if index is present to set additional context
          if (indexParam) {
            const index = parseInt(indexParam, 10);
            initialFirstLocal = Math.floor((index - 1) / recordsPerPageInitial) * recordsPerPageInitial + 1;
            initialSelectedRecordLocal = index;
            initialViewModeLocal = 'list';
          }
        } catch (error) {
          console.error("Error retrieving state from server:", error);
          initialCCObj = defaultCCObj;
        }
      } else {
        try {
          console.log("one")
          initialCCObj = ccUtils.deepMerge(defaultCCObj, JSON.parse(stateParam) as CC);
          initialViewModeLocal = 'list';
          console.log("two")
          if (indexParam) {
            console.log("three")
            const index = parseInt(indexParam, 10);
            initialFirstLocal = Math.floor((index - 1) / recordsPerPageInitial) * recordsPerPageInitial + 1;
            initialSelectedRecordLocal = index;
          }
        } catch (error) {
          console.warn("Failed to parse state:", error);
          initialCCObj = defaultCCObj;
        }
      }
    } else {
      if (compressedStateParam) {
        initialViewModeLocal = "list";
        try {
          decompressFromURI(compressedStateParam).then((stParam) => {
            initialCCObj = ccUtils.deepMerge(defaultCCObj, JSON.parse(stParam) as CC);
            initialViewModeLocal = 'list';
            if (indexParam) {
              const index = parseInt(indexParam, 10);
              initialFirstLocal = Math.floor((index - 1) / recordsPerPageInitial) * recordsPerPageInitial + 1;
              initialSelectedRecordLocal = index;
            }
        });
        } catch (error) {
          console.warn("Failed to parse compressed state:", error);
          initialCCObj = defaultCCObj;
        }
      }
      if (viewModeParam=='record') {
        initialSelectedRecordLocal = 1;
        setSelectedRecord(1);
      }
    }

    // Initialize facets and custom search criteria
    const updatedCustomSearchCriteria = initialCCObj.customSearchCriteria || [];
    const updatedFacets = initialCCObj.facets || {};

    if (monument) {
      updatedFacets.department = updatedFacets.department || [];
      if (!updatedFacets.department.includes(monument)) {
        updatedFacets.department.push(monument);
      }
    }

    if (theme) {
      if (theme === 'tapisseries') {
        const newCriterion = {
          field: '/record/theme',
          fieldLabel: 'Thème',
          searchValues: ["[thème: Tapisseries]"],
          description: 'La collection des tapisseries'
        };

        if (!updatedCustomSearchCriteria.some(
          criterion => criterion.field === newCriterion.field &&
            JSON.stringify(criterion.searchValues) === JSON.stringify(newCriterion.searchValues)
        )) {
          updatedCustomSearchCriteria.push(newCriterion);
        }
      }
    }

    if (id) {
      const newCriterion = {
        field: '/record/uniqid',
        fieldLabel: 'ID',
        searchValues: [id],
        description: ''
      };

      if (!updatedCustomSearchCriteria.some(
        criterion => criterion.field === newCriterion.field &&
          JSON.stringify(criterion.searchValues) === JSON.stringify(newCriterion.searchValues)
      )) {
        updatedCustomSearchCriteria.push(newCriterion);
      }
      initialSelectedRecordLocal = 1;
    }

    // Update CC object with facets and custom search criteria
    initialCCObj = {
      ...initialCCObj,
      facets: updatedFacets,
      customSearchCriteria: updatedCustomSearchCriteria
    };

    // Update state variables
    setCc(initialCCObj);
    setFirst(initialFirstLocal);
    console.log('initialSelectedRecordLocal', initialSelectedRecordLocal);
    setViewMode(initialViewModeLocal);

    // Set initialization complete
    setIsInitializing(false);

    setTimeout(() => {
      setSelectedRecord(initialSelectedRecordLocal);
    }, 500);

  };

  // Initialize CC on component mount
  useEffect(() => {
    initializeCC();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push state to URL when ready
  useEffect(() => {
    if (isReady && !isInitializing) {
      pushState({ cc, sort, first, recordsPerPage, viewMode, browseMode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setSelectedRecord(null); //disable record popup
  }, [cc, sort, first, recordsPerPage, viewMode, browseMode, isReady, isInitializing]);

  // Listen for URL state changes and update context accordingly
  useEffect(() => {
    if (isReady && location?.state) {
      const locationState = location.state as {
        cc: CC;
        sort: string;
        first: number;
        recordsPerPage: number;
        viewMode: string;
        browseMode: string;
      };

      const currentState = { cc, sort, first, recordsPerPage, viewMode, browseMode };

      if (JSON.stringify(locationState) !== JSON.stringify(currentState)) {
        prevCcPushed.current = JSON.stringify(locationState);
        setCc(locationState.cc);
        setSort(locationState.sort);
        setFirst(locationState.first);
        setRecordsPerPage(locationState.recordsPerPage);
        setViewMode(locationState.viewMode);
        setBrowseMode(locationState.browseMode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Function to perform search
  const search = async (currentCc: CC) => {
    const payload = await ccUtils.generatePayload(currentCc, sort, first, first + recordsPerPage - 1, user);
    try {
      const responseData = await ccUtils.postWithCache(process.env.REACT_APP_BASE_URL as string, payload, instanceRef);
      setData(responseData);
      setIsReady(true);
    } catch (error) {
      console.error(error);
    }
  };

  // Perform search when relevant state changes
  useEffect(() => {
    if (!isInitializing) {
      search(cc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cc, first, recordsPerPage, isInitializing]);

  return (
    <CCContext.Provider
      value={{
        cc,
        setCc,
        sort,
        setSort,
        first,
        setFirst,
        recordsPerPage,
        setRecordsPerPage,
        oldRecordsPerPage,
        setOldRecordsPerPage,
        viewMode,
        setViewMode,
        browseMode,
        setBrowseMode,
        data,
        setData,
        user,
        setUser,
        isReady,
        setReadyState,
        clearSearch,
        selectedRecord,
        setSelectedRecord,
        setCCContext,
      }}
    >
      {!isInitializing && children}
      {isInitializing && <div>Loading...</div>}
    </CCContext.Provider>
  );
};

export const useCCContext = () => {
  const context = useContext(CCContext);
  if (!context) {
    throw new Error('useCCContext must be used within a CCProvider');
  }
  return context;
};
